import {gql} from "@apollo/client";
import {mutate} from "../../lib/apollo";
import {searchBrokers} from "../blockedPublishers/blockedPublishersService";
import {getCompanies} from "../companies/companiesService";

export {searchBrokers};

/** Above this non-empty line count, paste stays in memory and is not written into the textarea DOM. */
export const LEADS_DOM_LINE_LIMIT = 500;

/** GraphQL payload chunk size for mark mutations. */
const MUTATION_CHUNK = 2000;

export type MarkConversionsResult = {
    matched: number;
    insertedOrUpdated: number;
    deleted: number;
};

export type ParsedLeads = {
    emails: string[];
    phones: number[];
    leadIds: number[];
    confirms: string[];
    invalidCount: number;
};

function isConfirmLike(line: string): boolean {
    return line.includes("-") || line.includes(">") || line.includes('"');
}

function isAllDigits(line: string): boolean {
    for (let i = 0; i < line.length; i++) {
        const c = line.charCodeAt(i);
        if (c < 48 || c > 57) {
            return false;
        }
    }
    return line.length > 0;
}

export function countNonEmptyLines(text: string): number {
    let count = 0;
    let lineHasContent = false;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (c === "\n") {
            if (lineHasContent) {
                count++;
            }
            lineHasContent = false;
        } else if (c !== "\r" && c !== " " && c !== "\t") {
            lineHasContent = true;
        }
    }
    if (lineHasContent) {
        count++;
    }
    return count;
}

function chunkArray<T>(items: T[], size: number): T[][] {
    if (items.length === 0) {
        return [];
    }
    const chunks: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size));
    }
    return chunks;
}

export function parseLeadLines(text: string, allowConfirms: boolean = true): ParsedLeads {
    const emails: string[] = [];
    const phones: number[] = [];
    const leadIds: number[] = [];
    const confirms: string[] = [];
    let invalidCount = 0;
    const seen = new Set<string>();

    let start = 0;
    for (let i = 0; i <= text.length; i++) {
        if (i !== text.length && text[i] !== "\n") {
            continue;
        }
        let a = start;
        let b = i;
        if (b > a && text[b - 1] === "\r") {
            b--;
        }
        while (a < b && (text[a] === " " || text[a] === "\t")) {
            a++;
        }
        while (b > a && (text[b - 1] === " " || text[b - 1] === "\t")) {
            b--;
        }
        start = i + 1;
        if (a >= b) {
            continue;
        }

        const line = text.slice(a, b);
        const key = line.toLowerCase();
        if (seen.has(key)) {
            invalidCount++;
            continue;
        }
        seen.add(key);

        if (line.includes("@")) {
            emails.push(line);
        } else if (isAllDigits(line)) {
            if (line.length === 10) {
                phones.push(Number(line));
            } else {
                leadIds.push(Number(line));
            }
        } else if (allowConfirms && isConfirmLike(line)) {
            confirms.push(line);
        } else {
            invalidCount++;
        }
    }

    return {emails, phones, leadIds, confirms, invalidCount};
}

export async function loadActiveCompanies() {
    const {companies} = await getCompanies();
    return (companies || []).filter((c: any) => c.active);
}

async function markConversionsOnce(
    isLinkout: boolean,
    input: {
        brokerId: number;
        companyId: number;
        conversionTypeId: number;
        emails: string[];
        phones: number[];
        leadIds: number[];
        confirms: string[];
    },
): Promise<MarkConversionsResult | null> {
    const q = isLinkout
        ? gql`
            mutation MarkConversionsLinkout($input: MarkConversionsInput!) {
                markConversionsLinkout(input: $input) {
                    matched
                    insertedOrUpdated
                    deleted
                }
            }
        `
        : gql`
            mutation MarkConversions($input: MarkConversionsInput!) {
                markConversions(input: $input) {
                    matched
                    insertedOrUpdated
                    deleted
                }
            }
        `;

    const response = await mutate(q, {input});
    const result = isLinkout ? response?.markConversionsLinkout : response?.markConversions;
    if (!result) {
        return null;
    }
    return result as MarkConversionsResult;
}

export async function markConversions(
    isLinkout: boolean,
    input: {
        brokerId: number;
        companyId: number;
        conversionTypeId: number;
        emails: string[];
        phones: number[];
        leadIds: number[];
        confirms: string[];
    },
    onProgress?: (doneChunks: number, totalChunks: number) => void,
): Promise<MarkConversionsResult | null> {
    const jobs: Array<Pick<typeof input, "emails" | "phones" | "leadIds" | "confirms">> = [
        ...chunkArray(input.emails, MUTATION_CHUNK).map((emails) => ({emails, phones: [] as number[], leadIds: [] as number[], confirms: [] as string[]})),
        ...chunkArray(input.phones, MUTATION_CHUNK).map((phones) => ({emails: [] as string[], phones, leadIds: [] as number[], confirms: [] as string[]})),
        ...chunkArray(input.leadIds, MUTATION_CHUNK).map((leadIds) => ({emails: [] as string[], phones: [] as number[], leadIds, confirms: [] as string[]})),
        ...chunkArray(input.confirms, MUTATION_CHUNK).map((confirms) => ({emails: [] as string[], phones: [] as number[], leadIds: [] as number[], confirms})),
    ];

    if (jobs.length === 0) {
        return {matched: 0, insertedOrUpdated: 0, deleted: 0};
    }

    const total = {matched: 0, insertedOrUpdated: 0, deleted: 0};
    for (let i = 0; i < jobs.length; i++) {
        const result = await markConversionsOnce(isLinkout, {
            brokerId: input.brokerId,
            companyId: input.companyId,
            conversionTypeId: input.conversionTypeId,
            ...jobs[i],
        });
        if (!result) {
            return total.matched || total.insertedOrUpdated || total.deleted ? total : null;
        }
        total.matched += result.matched;
        total.insertedOrUpdated += result.insertedOrUpdated;
        total.deleted += result.deleted;
        onProgress?.(i + 1, jobs.length);
        // Let the UI breathe between large chunks.
        if (jobs.length > 1) {
            await new Promise((r) => setTimeout(r, 0));
        }
    }
    return total;
}
